import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Phone, Linkedin, Github, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { motion, AnimatePresence, useMotionValue } from "framer-motion";
import { useTheme } from "../theme/ThemeContext";
import { theme } from "@/theme/theme";
import emailjs from "@emailjs/browser";

const Contact = () => {
  const { theme: themeMode } = useTheme();
  const contactStyles = theme.contact[themeMode];
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Mouse position tracking for 3D effect
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    mouseX.set(x);
    mouseY.set(y);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;
    const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
    const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;

    try {
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        message: formData.message,
        to_name: "Adarsh Parmar",
        reply_to: formData.email,
      };

      await emailjs.send(serviceId, templateId, templateParams, publicKey);

      toast({
        variant: "success",
        title: "Message sent successfully! ✅",
        description: "Thanks for reaching out! I'll get back to you soon.",
      });

      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      console.error("Email sending failed:", error);
      toast({
        variant: "destructive",
        title: "Failed to send message ❌",
        description:
          "Something went wrong. Please try again or contact me directly.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0, rotateX: -45 },
    visible: {
      y: 0,
      opacity: 1,
      rotateX: 0,
      transition: {
        duration: 0.8,
        ease: [0.6, -0.05, 0.01, 0.99],
      },
    },
  };

  const cardVariants = {
    hidden: { scale: 0.8, opacity: 0, rotateY: -45 },
    visible: {
      scale: 1,
      opacity: 1,
      rotateY: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
    hover: {
      scale: 1.05,
      rotateY: 5,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
      },
    },
  };

  const iconVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.5,
        ease: "backOut",
      },
    },
    hover: {
      scale: 1.2,
      rotate: 10,
      transition: {
        duration: 0.2,
        ease: "easeInOut",
      },
    },
  };

  const buttonVariants = {
    hidden: { scale: 0.9, opacity: 0, rotateX: -45 },
    visible: {
      scale: 1,
      opacity: 1,
      rotateX: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
    hover: {
      scale: 1.05,
      rotateX: 5,
      transition: {
        duration: 0.2,
        ease: "easeInOut",
      },
    },
    tap: {
      scale: 0.95,
      rotateX: -5,
      transition: {
        duration: 0.1,
      },
    },
  };

  return (
    <section
      id="contact"
      className={`py-24 px-4 sm:px-6 lg:px-8 relative font-mono perspective-1000 transition-colors duration-500 ${contactStyles.background}`}
    >
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
        className="max-w-6xl mx-auto relative z-10"
        onMouseMove={handleMouseMove}
      >
        <motion.div
          initial={{ opacity: 0, y: -50, rotateX: -45 }}
          whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.6, -0.05, 0.01, 0.99] }}
          className="text-center mb-20"
        >
          <motion.h2
            className={`text-4xl md:text-5xl font-bold mb-6 tracking-tight ${contactStyles.text.heading}`}
            style={{
              transformStyle: "preserve-3d",
              transform: "perspective(1000px)",
            }}
          >
            <motion.span
              className={`${contactStyles.text.code} inline-block`}
              initial={{ opacity: 0, x: -50, rotateY: -45 }}
              whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              &lt;
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              Get In Touch
            </motion.span>
            <motion.span
              className={`${contactStyles.text.code} inline-block`}
              initial={{ opacity: 0, x: 50, rotateY: 45 }}
              whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              /&gt;
            </motion.span>
          </motion.h2>
          <motion.div
            className={`w-32 h-1 bg-gradient-to-r ${contactStyles.button.primary} mx-auto mb-8`}
            initial={{ width: 0, rotateX: -45 }}
            whileInView={{ width: "8rem", rotateX: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
          />
          <motion.p
            className={`${contactStyles.text.secondary} text-lg max-w-2xl mx-auto leading-relaxed`}
            initial={{ opacity: 0, y: 30, rotateX: -45 }}
            whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            Ready to start your next project? Let's discuss how we can work
            together to bring your ideas to life.
          </motion.p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16"
        >
          {/* Contact Info */}
          <motion.div variants={itemVariants} className="space-y-8">
            <div>
              <motion.h3
                className={`text-2xl font-bold mb-8 tracking-wide ${contactStyles.text.heading}`}
                initial={{ opacity: 0, x: -30, rotateY: -45 }}
                whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <motion.span
                  className={contactStyles.text.code}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  #
                </motion.span>{" "}
                Contact Information
              </motion.h3>

              <div className="space-y-4 sm:space-y-6">
                <AnimatePresence>
                  {[
                    {
                      icon: Mail,
                      text: "adarshparmar.business@gmail.com",
                      link: null,
                    },
                    { icon: Phone, text: "+91 9724397749", link: null },
                    {
                      icon: Linkedin,
                      text: "LinkedIn Profile",
                      link: "https://www.linkedin.com/in/adarsh-parmar-161960288",
                    },
                    {
                      icon: Github,
                      text: "GitHub Profile",
                      link: "https://github.com/adarsh25-dev",
                    },
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      variants={cardVariants}
                      initial="hidden"
                      whileInView="visible"
                      whileHover="hover"
                      viewport={{ once: true }}
                      className={`flex items-start sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 ${contactStyles.card.background} ${contactStyles.card.border} border rounded-lg ${contactStyles.card.hover.border} ${contactStyles.card.hover.shadow} backdrop-blur-sm`}
                      style={{
                        transformStyle: "preserve-3d",
                        transform: "perspective(1000px)",
                      }}
                    >
                      <motion.div
                        variants={iconVariants}
                        className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full ${contactStyles.icon.background} ${contactStyles.icon.hover} flex items-center justify-center shrink-0`}
                        style={{
                          transformStyle: "preserve-3d",
                          transform: "perspective(1000px)",
                        }}
                      >
                        <item.icon
                          className={`w-5 h-5 sm:w-6 sm:h-6 ${contactStyles.icon.color}`}
                        />
                      </motion.div>
                      {item.link ? (
                        <a
                          href={item.link}
                          className={`${contactStyles.link.color} ${contactStyles.link.hover} font-medium text-sm sm:text-base break-all`}
                        >
                          {item.text}
                        </a>
                      ) : (
                        <span
                          className={`${contactStyles.text.secondary} font-medium text-sm sm:text-base break-words`}
                        >
                          {item.text}
                        </span>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            variants={itemVariants}
            className={`${contactStyles.card.background} ${contactStyles.card.border} border rounded-lg p-4 sm:p-6 lg:p-8 ${contactStyles.card.hover.border} ${contactStyles.card.hover.shadow} shadow-lg backdrop-blur-sm`}
            style={{
              transformStyle: "preserve-3d",
              transform: "perspective(1000px)",
            }}
          >
            <motion.h3
              className={`text-2xl font-bold mb-8 tracking-wide ${contactStyles.text.heading}`}
              initial={{ opacity: 0, x: 30, rotateY: 45 }}
              whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <motion.span
                className={contactStyles.text.code}
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                #
              </motion.span>{" "}
              Send a Message
            </motion.h3>

            <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 30, rotateX: -45 }}
                whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <Label
                  htmlFor="name"
                  className={`${contactStyles.text.secondary} font-medium mb-2 block`}
                >
                  Name
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={`${contactStyles.input.background} ${contactStyles.input.border} ${contactStyles.text.main} placeholder-gray-400 mt-2 h-11 sm:h-12 ${contactStyles.input.hover} font-mono rounded-md text-sm sm:text-base`}
                  placeholder="Your full name"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30, rotateX: -45 }}
                whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <Label
                  htmlFor="email"
                  className={`${contactStyles.text.secondary} font-medium mb-2 block`}
                >
                  Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={`${contactStyles.input.background} ${contactStyles.input.border} ${contactStyles.text.main} placeholder-gray-400 mt-2 h-11 sm:h-12 ${contactStyles.input.hover} font-mono rounded-md text-sm sm:text-base`}
                  placeholder="your.email@example.com"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30, rotateX: -45 }}
                whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.5 }}
              >
                <Label
                  htmlFor="message"
                  className={`${contactStyles.text.secondary} font-medium mb-2 block`}
                >
                  Message
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className={`${contactStyles.input.background} ${contactStyles.input.border} ${contactStyles.text.main} placeholder-gray-400 mt-2 min-h-[120px] sm:min-h-[140px] ${contactStyles.input.hover} font-mono rounded-md resize-none text-sm sm:text-base`}
                  placeholder="Tell me about your project..."
                />
              </motion.div>

              <motion.div
                variants={buttonVariants}
                initial="hidden"
                whileInView="visible"
                whileHover="hover"
                whileTap="tap"
                viewport={{ once: true }}
                className="pt-4"
              >
                <Button
                  type="submit"
                  className={`w-full bg-gradient-to-r ${contactStyles.button.primary} hover:${contactStyles.button.hover} ${contactStyles.button.text} ${contactStyles.button.hoverText} min-h-11 sm:min-h-12 px-4 py-3 text-sm sm:text-base font-bold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed`}
                  style={{
                    transformStyle: "preserve-3d",
                    transform: "perspective(1000px)",
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4 mr-2" />
                      Sending...
                    </>
                  ) : (
                    "Send Message"
                  )}
                </Button>
              </motion.div>
            </form>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Contact;
